import { useFeatureMutation } from '@common/hooks/query/useFeatureMutation';
import { mergeWithDefault } from '@common/utils';
import { useLanguage } from '@features/language';
import { useSaveCollectionOverridesMutation } from '../api';
import {
    DEFAULT_MULTILINGUAL_METADATA,
    SaveCollectionOverrideArgs,
    SaveCollectionOverrideResponse,
} from '../types';
import { flattenMetadata } from '../utils/mappers';

const LOCALIZED_FIELDS = new Set(['title', 'genres', 'synopsis']);

export const useSaveCollectionOverrides = () => {
    const {
        currentLanguage: { code: locale },
    } = useLanguage();

    const { execute: rawExecute, ...mutationState } = useFeatureMutation<
        SaveCollectionOverrideArgs,
        SaveCollectionOverrideResponse
    >(useSaveCollectionOverridesMutation);

    const execute = async (args: SaveCollectionOverrideArgs) => {
        const localizedOverrides = Object.fromEntries(
            Object.entries(args.overrides).map(([key, value]) => [
                key,
                LOCALIZED_FIELDS.has(key) ? { [locale]: value } : value,
            ]),
        );

        const response = await rawExecute({
            ...args,
            overrides:
                localizedOverrides as SaveCollectionOverrideArgs['overrides'],
        });

        const safeMultilingualData = mergeWithDefault(
            response?.savedData,
            DEFAULT_MULTILINGUAL_METADATA,
        );

        return {
            ...response,
            savedData: flattenMetadata(safeMultilingualData, locale),
        };
    };

    return { execute, ...mutationState };
};
